import {
    Box, Button, FormControl, FormLabel, Grid,
    NumberDecrementStepper,
    NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper,
    Slider, SliderFilledTrack, SliderThumb, SliderTrack, Switch, Text,
    useColorModeValue, useDisclosure, useToast, Flex, Spinner, Alert, AlertIcon
} from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import { useState } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { useGetEloSettings } from "../../organization/hooks/useGetEloSettings";
import { useUpdateEloSettings } from "../../organization/hooks/useUpdateEloSettings";
import EloSimulator, { EloFormula } from "./EloSimulator";

export default function EloSettingsCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();
    const orgId = selectedOrganization?.id || '';

    const { data: eloSettings, isLoading: isLoadingSettings } = useGetEloSettings(orgId);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [kFactor, setKFactor] = useState(0.5);
    const [scalingFactor, setScalingFactor] = useState(1.2);
    const [marginOfVictoryEnabled, setMarginOfVictoryEnabled] = useState(true);
    const [maxLevelAdjustment, setMaxLevelAdjustment] = useState(0.5);

    const { mutateAsync: updateSettings, isLoading: isSaving } =
        useUpdateEloSettings(orgId, onClose);

    const handleOpen = () => {
        if (eloSettings) {
            setKFactor(eloSettings.eloKFactor);
            setScalingFactor(eloSettings.eloScalingFactor);
            setMarginOfVictoryEnabled(eloSettings.eloMarginOfVictoryEnabled);
            setMaxLevelAdjustment(eloSettings.eloMaxLevelAdjustment);
        }
        onOpen();
    };

    const handleSave = async () => {
        try {
            await updateSettings({
                eloKFactor: kFactor,
                eloScalingFactor: scalingFactor,
                eloMarginOfVictoryEnabled: marginOfVictoryEnabled,
                eloMaxLevelAdjustment: maxLevelAdjustment,
            });
            toast({
                title: translate('organization.elo_settings_updated'),
                status: 'success',
                duration: 3000,
                position: 'top-right',
            });
        } catch {
            toast({ title: translate('error'), status: 'error', duration: 3000, position: 'top-right' });
        }
    };

    const handleResetDefaults = () => {
        setKFactor(0.5);
        setScalingFactor(1.2);
        setMarginOfVictoryEnabled(true);
        setMaxLevelAdjustment(0.5);
    };

    if (!selectedOrganization) return null;

    return (
        <>
            <PageCard title={translate('organization.elo_settings')}>
                <Box p={4}>
                    {isLoadingSettings ? (
                        <Spinner />
                    ) : eloSettings ? (
                        <>
                            <Box mb={4} color={textColorPrimary}>
                                <Box mb={2}>
                                    <strong>{translate('organization.elo_k_factor')}: </strong>
                                    {eloSettings.eloKFactor}
                                </Box>
                                <Box mb={2}>
                                    <strong>{translate('organization.elo_scaling_factor')}: </strong>
                                    {eloSettings.eloScalingFactor}
                                </Box>
                                <Box mb={2}>
                                    <strong>{translate('organization.elo_margin_of_victory')}: </strong>
                                    {eloSettings.eloMarginOfVictoryEnabled
                                        ? translate('common.yes')
                                        : translate('common.no')}
                                </Box>
                                <Box mb={2}>
                                    <strong>{translate('organization.elo_max_adjustment')}: </strong>
                                    {eloSettings.eloMaxLevelAdjustment}
                                </Box>
                            </Box>
                            <Button mt={3} size="md" onClick={handleOpen} colorScheme="brand">
                                {translate('organization.elo_edit_settings')}
                            </Button>
                        </>
                    ) : (
                        <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            {translate('error')}
                        </Alert>
                    )}
                </Box>
            </PageCard>

            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                title={translate('organization.elo_settings')}
                onSubmit={handleSave}
                submitLabel={translate('save')}
                cancelLabel={translate('common.cancel')}
                isSubmitting={isSaving}
                size="5xl"
                destructiveAction={{
                    label: translate('organization.elo_reset_defaults'),
                    onClick: handleResetDefaults,
                }}
            >
                <EloFormula
                    kFactor={kFactor}
                    scalingFactor={scalingFactor}
                    marginOfVictoryEnabled={marginOfVictoryEnabled}
                    maxLevelAdjustment={maxLevelAdjustment}
                />
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={5}>
                    {/* Left column: settings */}
                    <Box>
                        <FormControl mb={3}>
                            <FormLabel>{translate('organization.elo_k_factor')}</FormLabel>
                            <Text fontSize="xs" color="gray.500" mb={1}>
                                {translate('organization.elo_k_factor_desc')}
                            </Text>
                            <Flex gap={4} align="center">
                                <Slider
                                    flex={1}
                                    value={kFactor}
                                    onChange={setKFactor}
                                    min={0.1}
                                    max={2.0}
                                    step={0.1}
                                >
                                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                                    <SliderThumb />
                                </Slider>
                                <NumberInput
                                    w="80px"
                                    value={kFactor}
                                    onChange={(_, val) => { if (!isNaN(val)) setKFactor(val); }}
                                    min={0.1}
                                    max={2.0}
                                    step={0.1}
                                    precision={1}
                                >
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </Flex>
                        </FormControl>

                        <FormControl mb={3}>
                            <FormLabel>{translate('organization.elo_scaling_factor')}</FormLabel>
                            <Text fontSize="xs" color="gray.500" mb={1}>
                                {translate('organization.elo_scaling_factor_desc')}
                            </Text>
                            <Flex gap={4} align="center">
                                <Slider
                                    flex={1}
                                    value={scalingFactor}
                                    onChange={setScalingFactor}
                                    min={0.5}
                                    max={3.0}
                                    step={0.1}
                                >
                                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                                    <SliderThumb />
                                </Slider>
                                <NumberInput
                                    w="80px"
                                    value={scalingFactor}
                                    onChange={(_, val) => { if (!isNaN(val)) setScalingFactor(val); }}
                                    min={0.5}
                                    max={3.0}
                                    step={0.1}
                                    precision={1}
                                >
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </Flex>
                        </FormControl>

                        <FormControl mb={3} display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <FormLabel mb={0}>{translate('organization.elo_margin_of_victory')}</FormLabel>
                                <Text fontSize="xs" color="gray.500">
                                    {translate('organization.elo_margin_of_victory_desc')}
                                </Text>
                            </Box>
                            <Switch
                                isChecked={marginOfVictoryEnabled}
                                onChange={(e) => setMarginOfVictoryEnabled(e.target.checked)}
                                colorScheme="brand"
                            />
                        </FormControl>

                        <FormControl mb={3}>
                            <FormLabel>{translate('organization.elo_max_adjustment')}</FormLabel>
                            <Text fontSize="xs" color="gray.500" mb={1}>
                                {translate('organization.elo_max_adjustment_desc_before')} <strong>{maxLevelAdjustment}</strong> {translate('organization.elo_max_adjustment_desc_mid')} <strong>{maxLevelAdjustment}</strong> {translate('organization.elo_max_adjustment_desc_after')}
                            </Text>
                            <Flex gap={4} align="center">
                                <Slider
                                    flex={1}
                                    value={maxLevelAdjustment}
                                    onChange={setMaxLevelAdjustment}
                                    min={0.1}
                                    max={1.5}
                                    step={0.1}
                                >
                                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                                    <SliderThumb />
                                </Slider>
                                <NumberInput
                                    w="80px"
                                    value={maxLevelAdjustment}
                                    onChange={(_, val) => { if (!isNaN(val)) setMaxLevelAdjustment(val); }}
                                    min={0.1}
                                    max={1.5}
                                    step={0.1}
                                    precision={1}
                                >
                                    <NumberInputField />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                            </Flex>
                        </FormControl>
                    </Box>

                    {/* Right column: formula + examples + simulator */}
                    <Box>
                        <EloSimulator
                            kFactor={kFactor}
                            scalingFactor={scalingFactor}
                            marginOfVictoryEnabled={marginOfVictoryEnabled}
                            maxLevelAdjustment={maxLevelAdjustment}
                        />
                    </Box>
                </Grid>
            </FormModal>
        </>
    );
}
